import { Button } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../../api";
import { useToast } from "../../../mantine/notifications/useToast";
import type { components } from "../../../types/api.types";
import { FoerderRegime } from "../../../types/api.types";
import urls from "../../../urls";
import type {
  VBHCalculated,
  VBHCalculatedResponseData
} from "../../../utils/backend-types";
import { THIS_YEAR } from "../../../utils/dates";
import { IconHelpText } from "../../IconHelpText/IconHelpText";
import { OrangeCircle } from "../../OrangeCircle/OrangeCircle";
import { VollbenutzungsstundenInfo } from "./VollbenutzungsstundenInfo/VollbenutzungsstundenInfo";
import "./VollbenutzungsstundenList.scss";
import { VollbenutzungsstundenTable } from "./VollbenutzungsstundenTable/VollbenutzungsstundenTable";

const MAX_HOURS = 30000;

export type Vollbenutzungsstunden =
  components["schemas"]["KWKAnlage"]["vollbenutzungsstunden"];

export type VollbenutzungsstundenItem = Vollbenutzungsstunden[number];

const VBH_START_YEAR = 2020;
const VBH_HELP_TEXT =
  "Der Zuschlag nach KWKG wird für KWK-Anlagen bis zu 30.000 Vollbenutzungsstunden (Vbh) gewährt. " +
  "Für entsprechende Meldungen können hier die Vbh eingetragen werden.";

interface VollbenutzungsstundenListProps {
  kwkAnlagenId: number;
  vollbenutzungsstunden: Vollbenutzungsstunden;
  foerderregime: FoerderRegime;
  onInput: (field: string, value: Vollbenutzungsstunden) => void;
  missingFields?: Array<string>;
}

function VollbenutzungsstundenList({
  kwkAnlagenId,
  vollbenutzungsstunden,
  foerderregime,
  onInput,
  missingFields
}: VollbenutzungsstundenListProps) {
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  //This is hack as we do not have the KWK id in the software.
  const { t } = useTranslation();
  const { showToast, showErrorToast } = useToast();

  const vbhListInitial =
    vollbenutzungsstunden.length === 0
      ? [{ year: VBH_START_YEAR, hours: null }]
      : vollbenutzungsstunden;
  const vbhList = addMissingYearsToVbhList(vbhListInitial);

  function getUpdatedVbhList(
    currentVBH: VollbenutzungsstundenItem,
    newHours: number | null
  ): Vollbenutzungsstunden {
    const newVBHList = [...vbhList];
    const vbhIndex = newVBHList.findIndex((a) => a.year === currentVBH.year);

    if (vbhIndex >= 0) {
      newVBHList[vbhIndex].hours = newHours;
    }

    return newVBHList;
  }

  function removeNotNeededYears(
    vbhArr: Vollbenutzungsstunden
  ): Vollbenutzungsstunden {
    const newVBHList = [...vbhArr];
    const exceededRow = computeCumulativeVBH(newVBHList).find(maxHoursExceeded);

    if (exceededRow) {
      const lastCorrectYear = exceededRow.year;
      for (let i = newVBHList.length - 1; i >= 0; i--) {
        if (newVBHList[i].year > lastCorrectYear) {
          newVBHList.splice(i, 1);
        }
      }
    }

    return newVBHList;
  }

  function updateValues(
    currentVBH: VollbenutzungsstundenItem,
    newHours: number | null
  ) {
    const newVBHList = removeNotNeededYears(
      getUpdatedVbhList(currentVBH, newHours)
    );

    onInput("vollbenutzungsstunden", newVBHList);
  }

  const yearsInPastWithNoValues = vbhList
    .filter((vbh) => !vbh.hours && vbh.year < THIS_YEAR)
    .map((vbh) => vbh.year);

  function handleClickCalculate() {
    const yearsWithNoValues = yearsInPastWithNoValues;
    setCalculationLoading(true);
    api
      .get<VBHCalculatedResponseData>(
        urls.api.calculateVBH(kwkAnlagenId, yearsWithNoValues)
      )
      .then((response) => {
        handleVBHCalculatedData(response.data);
      })
      .catch((error) => {
        showErrorToast(error);
      })
      .finally(() => {
        setCalculationLoading(false);
      });
  }

  function handleVBHCalculatedData(data: VBHCalculatedResponseData) {
    Object.keys(data).forEach((currentYear) => {
      const newData: VBHCalculated = data[currentYear];
      if (newData.status === "SUCCESS") {
        const vbhForYear = vbhList.find(
          (vbh) => vbh.year === parseInt(currentYear)
        );

        if (vbhForYear) {
          updateValues(vbhForYear, data[currentYear].value);

          showToast({
            type: "success",
            message: t("messages.Generator.VBHCalculated", {
              year: currentYear
            })
          });
        }
      } else {
        showErrorToast(newData.message);
      }
    });
  }
  const isCorrectFoerderregime =
    foerderregime === FoerderRegime.kwkg_zuschlag_2012 ||
    foerderregime === FoerderRegime.kwkg_ausschreibung ||
    foerderregime === FoerderRegime.kwkg_zuschlag_2016_2020 ||
    foerderregime === FoerderRegime.kwkg_zuschlag_2000_2002_2009;
  return (
    isCorrectFoerderregime && (
      <div className="vbl-container">
        <div className="vbl-control-and-help-container">
          {showInfo && (
            <div>
              <VollbenutzungsstundenInfo onToggle={() => setShowInfo(false)} />
            </div>
          )}

          <div className="vbl-help-text-container">
            <div>
              {missingFields?.includes("vollbenutzungsstunden") && (
                <OrangeCircle />
              )}
            </div>
            <IconHelpText helpText={VBH_HELP_TEXT} />
          </div>
          <div className="vbl-controls-container">
            <Button
              color="primary"
              disabled={yearsInPastWithNoValues.length === 0}
              loading={calculationLoading}
              onClick={handleClickCalculate}
            >
              Berechnung anstoßen
            </Button>
          </div>
        </div>
        <VollbenutzungsstundenTable
          vollbenutzungsstunden={vbhList}
          onUpdateValues={updateValues}
        />
      </div>
    )
  );
}

function addMissingYearsToVbhList(currentVbhList: Vollbenutzungsstunden) {
  if (computeCumulativeVBH(currentVbhList).some(maxHoursExceeded)) {
    return currentVbhList;
  }

  const latestYear = currentVbhList.reduce((a, b) =>
    a.year > b.year ? a : b
  ).year;

  if (latestYear === THIS_YEAR) {
    return currentVbhList;
  }

  const newVBHList = [...currentVbhList];
  const yearDifference = THIS_YEAR - latestYear;

  for (let i = 1; i <= yearDifference; i++) {
    newVBHList.push({ year: latestYear + i, hours: null });
  }

  return newVBHList;
}

function maxHoursExceeded(vbh: VollbenutzungsstundenItem) {
  return vbh.hours && vbh.hours >= MAX_HOURS;
}

export function computeCumulativeVBH(
  vbh: Vollbenutzungsstunden
): Vollbenutzungsstunden {
  return vbh
    .sort((a, b) => a.year - b.year)
    .map((value, index, array) => {
      return {
        year: value.year,
        hours: sumUpToIndex(array, index)
      };
    });
}
function sumUpToIndex(arr: Vollbenutzungsstunden, index: number): number {
  // Use the reduce method to sum up the values.
  return arr.reduce((acc, current, currentIndex) => {
    if (currentIndex <= index) {
      return acc + (current.hours ?? 0);
    } else {
      return acc;
    }
  }, 0);
}

export { VollbenutzungsstundenList, VollbenutzungsstundenListProps };
